import orderBy from 'lodash/orderBy';
import { TNoteGroup, TNote, Assignee } from './types';
import { User } from '@/_models/User';
import { teams } from './data/task-manager';

const formatGroupName = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

export const groupNotesByDays = (notes: TNote[]): TNoteGroup[] => {
  const sortedNotes = orderBy(notes, 'createdAt', 'asc'); // Changed to 'asc'

  return sortedNotes.reduce<TNoteGroup[]>((grouped, note) => {
    const createdDate = new Date(note.createdAt);
    // Reset time to start of day for grouping
    const dayDate = new Date(
      createdDate.getFullYear(),
      createdDate.getMonth(),
      createdDate.getDate(),
    );

    // Find existing group or create new one
    let group = grouped.find((g) => g.key === dayDate.getTime());

    if (!group) {
      group = {
        key: dayDate.getTime(),
        groupName: formatGroupName(dayDate),
        notes: [],
      };
      grouped.push(group);
    }

    group.notes.push(note);

    return orderBy(grouped, ['day'], ['asc']).map((g) => ({
      ...g,
      notes: orderBy(g.notes, ['createdAt'], ['asc']),
    }));
  }, []);
};

export const filterNotesByCreatedAtDateRange = <T extends TNote>(
  threads: T[],
  dateRange: [Date, Date],
): T[] => {
  const [startDate, endDate] = dateRange;

  // Reset times to start/end of day for accurate comparison
  const rangeStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const rangeEnd = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
    23,
    59,
    59,
    999,
  );

  return threads.filter((thread) => {
    const threadDate = new Date(thread.createdAt);
    return threadDate >= rangeStart && threadDate <= rangeEnd;
  });
};

export const renderHighlightedTextWithHTML = (text: string, collaborators: { name: string }[]) => {
  const regex = /@([\w.]+(?:\s+[\w.]+)?)/g;
  const highlightedText = text.replace(regex, (match, tag) => {
    const isCollaborator = collaborators.some((collab) => collab.name === tag.trim());
    if (isCollaborator) {
      return `<span style="color: blue;">${match}</span>`;
    }
    return match;
  });

  return { __html: highlightedText };
};

export const getAssignee = (id: string | number, collaborators: User[]): Assignee | null => {
  const collaborator = collaborators.find((c) => c.id === Number(id) || c.name === id);
  if (collaborator)
    return {
      id: collaborator.id.toString(),
      name: collaborator.name,
      color: collaborator.primaryColor,
      avatarUrl: collaborator.profile,
    };

  return teams.find((t) => t.id === id);
};
